import { Box } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { PostSubTypes, usePostDetails } from 'api';
// import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { GoBackButtonWithTitle, NewConfirmDialog, SeeMoreModal, Spinner } from 'components';
import { AddCommentForm, CommentsList } from 'features/comments';
import { useModal, useWindowSize } from 'hooks';
import { getFullName } from 'utils';

import { PostDetails } from '../../components';
import { SeeMoreLink } from '../../components/PostDetails/styled';
import { ContentW, ContentWithRemoveW, PageW } from './styled';
import { usePostDetailsPage } from './usePostDetailsPage';

export function PostDetailsPage(): ReactElement {
  const { postId = '', taskId = '' } = useParams<string>();
  const { t } = useTranslation();

  const {
    isLoading,
    navigateBack,
    post,
    categoriesList,
    comments,
    isLoadingComments,
    isFetchingNextPage,
    lastItemRef,
    isPoll,
    isTask,
    canEdit,
    canDelete,
    handleOpenModal,
    isOpen,
    handleDeletePost,
    handleCloseModal,
    handleOpenEditMode,
  } = usePostDetailsPage(postId || taskId, !!taskId);

  const { isMobile } = useWindowSize();

  const {
    isOpen: isOpenCommentsModal,
    handleOpenModal: handleOpenCommentsModal,
    handleCloseModal: handleCloseCommentsModal,
  } = useModal();

  const { post: poll } = usePostDetails({
    postId: post?.meta?.poll_id || '',
    queryName: `post-details, ${post?.meta?.poll_id || ''}`,
    include_reactions: true,
  });

  const isFinishedPoll = post?.meta?.sub_type == 'poll_finished';
  const showDeleteButton = !isFinishedPoll && canDelete;

  if (isLoading) {
    return <Spinner />;
  }

  if (!post) {
    return <PageW />;
  }

  const { title, author, id, meta } = post;
  const isWelcomePost = post.meta?.sub_type === PostSubTypes.WELCOME;
  const isBirthdayPost = post.meta?.sub_type === PostSubTypes.BIRTHDAY;
  const isPollPost = post.meta?.sub_type === PostSubTypes.POLL;
  const isKpiTargetPost = Boolean(meta?.kpi_target_id && meta.sub_type === PostSubTypes.KPI_TARGET);

  const removeDialogTitle = isTask
    ? t('tasks.details.removeModal.title', { taskName: title })
    : isPoll
    ? t('poll.removeModal.title')
    : t('post.details.removeModal.title', { postTitle: title ? `”${title}”` : '' });
  const removeDialogSubTitle = isTask
    ? t('tasks.details.removeModal.subtitle')
    : isPoll
    ? t('poll.removeModal.subtitle')
    : t('post.details.removeModal.subtitle');
  const removeDialogSuccessButton = isTask
    ? t('tasks.details.removeModal.successButton')
    : isPoll
    ? t('poll.removeModal.successButton')
    : t('post.details.removeModal.successButton');
  const removeDialogCancelButton = isTask
    ? t('tasks.details.removeModal.cancelButton')
    : isPoll
    ? t('poll.removeModal.cancelButton')
    : t('post.details.removeModal.cancelButton');

  const TITLE = isWelcomePost
    ? t('post.listItem.welcomePost.title', {
        user: getFullName({ firstName: author.first_name, lastName: author.last_name }),
      })
    : isKpiTargetPost
    ? t('completedKpiTargetPost.title')
    : isBirthdayPost
    ? t('post.listItem.birthdayPost.title')
    : isPollPost
    ? t('post.listItem.pollPost.title', {
        title: poll?.title,
      })
    : title;

  return (
    <PageW>
      <ContentWithRemoveW>
        <ContentW isPollPost={isPoll}>
          <GoBackButtonWithTitle
            title={t('common.back')}
            onClick={navigateBack}
            containerSx={{
              marginTop: 0,
            }}
          />
          <PostDetails
            post={post}
            categoriesList={categoriesList}
            isWelcomePost={isWelcomePost}
            isBirthdayPost={isBirthdayPost}
            isPollPost={isPollPost}
            isKpiTargetPost={isKpiTargetPost}
            targetId={meta?.kpi_target_id}
            title={TITLE}
            canDelete={canDelete}
            canEdit={canEdit}
            showDeleteButton={showDeleteButton}
            onEdit={handleOpenEditMode}
            onDelete={handleOpenModal}
          />

          {comments.length ? (
            <CommentsList
              postId={id}
              postAuthor={author.id}
              comments={isMobile ? comments.slice(0, 1) : comments}
              isLoading={isLoadingComments}
              isFetchingNextPage={isMobile ? false : isFetchingNextPage}
              lastItemRef={isMobile ? undefined : lastItemRef}
            />
          ) : (
            <></>
          )}

          {isMobile && comments.length > 1 && (
            <SeeMoreLink onClick={handleOpenCommentsModal}>See more comments</SeeMoreLink>
          )}
          <Box sx={{ marginTop: '16px' }}>
            <AddCommentForm postId={id} isPostComment />
          </Box>
        </ContentW>
      </ContentWithRemoveW>

      <NewConfirmDialog
        title={removeDialogTitle}
        subTitle={removeDialogSubTitle}
        open={isOpen}
        onConfirm={handleDeletePost}
        onClose={handleCloseModal}
        submitLabel={removeDialogSuccessButton}
        cancelLabel={removeDialogCancelButton}
      />

      {isOpenCommentsModal && (
        <SeeMoreModal
          open={isOpenCommentsModal}
          onClose={handleCloseCommentsModal}
          title="Comments available on this post"
        >
          <CommentsList
            postId={id}
            comments={comments}
            postAuthor={author.id}
            isLoading={isLoadingComments}
            isFetchingNextPage={isFetchingNextPage}
            lastItemRef={lastItemRef}
            inModal
          />
        </SeeMoreModal>
      )}
    </PageW>
  );
}
